import { useState, useRef, useEffect } from "react";
import { MapPin, X, Loader2, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useLocationSearch } from "@/hooks/useLocationSearch";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const distanceOptions = [
  { value: 3, label: "3 mi" },
  { value: 5, label: "5 mi" },
  { value: 10, label: "10 mi" },
  { value: 15, label: "15 mi" },
  { value: 25, label: "25 mi" },
  { value: 50, label: "50 mi" },
];

interface LocationSearchInputProps {
  onLocationSelect: (location: { lat: number; lng: number; description: string } | null) => void;
  className?: string;
  showDistanceSelector?: boolean;
  distanceRadius?: number;
  onDistanceChange?: (distance: number) => void;
  onInputFocus?: () => void;
  onInputBlur?: () => void;
}

export function LocationSearchInput({ 
  onLocationSelect, 
  className,
  showDistanceSelector = false,
  distanceRadius = 5,
  onDistanceChange,
  onInputFocus,
  onInputBlur,
}: LocationSearchInputProps) {
  const [inputValue, setInputValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const {
    predictions,
    isSearching,
    selectedLocation,
    searchLocations,
    selectLocation,
    clearLocation,
  } = useLocationSearch();

  // Debounced search
  useEffect(() => {
    if (!inputValue || selectedLocation) return;
    
    const timer = setTimeout(() => {
      searchLocations(inputValue);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [inputValue, selectedLocation, searchLocations]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = async (placeId: string, description: string) => {
    const result = await selectLocation(placeId, description);
    if (result) {
      setInputValue(description);
      setShowDropdown(false);
      onLocationSelect(result);
    }
  };

  const handleClear = () => {
    setInputValue("");
    clearLocation();
    onLocationSelect(null);
    inputRef.current?.focus();
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
    setShowDropdown(true);
    if (selectedLocation) {
      clearLocation();
      onLocationSelect(null);
    }
  };

  return (
    <div className={cn("relative", className)}>
      <div className="relative flex items-center">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Enter city or zip code..."
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => {
            setShowDropdown(true);
            onInputFocus?.();
          }}
          onBlur={() => {
            onInputBlur?.();
          }}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          data-form-type="other"
          inputMode="search"
          className={cn(
            "pl-10",
            showDistanceSelector ? "pr-24" : "pr-10"
          )}
        />
        
        {/* Right side controls container */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {/* Distance selector - inline with input */}
          {showDistanceSelector && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-7 px-2 text-xs font-medium text-muted-foreground hover:text-foreground gap-1"
                >
                  <span>{distanceRadius} mi</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-card min-w-[100px]">
                {distanceOptions.map(({ value, label }) => (
                  <DropdownMenuItem 
                    key={value}
                    onClick={() => onDistanceChange?.(value)}
                    className={cn(
                      "cursor-pointer",
                      distanceRadius === value && "bg-muted font-medium"
                    )}
                  >
                    {label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          
          {/* Clear/Loading button */}
          {(inputValue || isSearching) && (
            <>
              {isSearching ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={handleClear}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Dropdown */}
      {showDropdown && !selectedLocation && inputValue.length >= 2 && (
        <div
          ref={dropdownRef}
          className="absolute z-[60] top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg overflow-hidden"
        >
          {predictions.length > 0 ? (
            predictions.map((prediction) => (
              <button
                key={prediction.place_id}
                onClick={() => handleSelect(prediction.place_id, prediction.description)}
                className="w-full text-left px-4 py-3 text-sm hover:bg-muted/50 transition-colors flex items-center gap-3"
              >
                <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="truncate">{prediction.description}</span>
              </button>
            ))
          ) : !isSearching ? (
            <div className="px-4 py-3 text-sm text-muted-foreground">
              No locations found
            </div>
          ) : null}
        </div>
      )}

    </div>
  );
}